import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Input,
  Skeleton,
  FieldError,
} from '@/components/ui';
import { Zap } from 'lucide-react';
import { partnerApi } from '@/lib/api-affiliate';
import { formatCents } from '@/lib/format';

interface AutoPayoutFields {
  autoPayoutEnabled?: boolean | null;
  autoPayoutThresholdCents?: number | null;
}

/** AutoPayoutToggle — compact card for toggling and configuring auto-payouts. */
export default function AutoPayoutToggle() {
  const balanceQ = partnerApi.balance.useGet();
  const minQ = partnerApi.minPayoutProgress.useGet();
  const update = partnerApi.autoPayout.useUpdate();

  const extended = (balanceQ.data ?? {}) as AutoPayoutFields;
  const initialEnabled = !!extended.autoPayoutEnabled;
  const initialThreshold = extended.autoPayoutThresholdCents ?? 0;
  const minPayoutCents = minQ.data?.minPayoutCents ?? 0;

  const [enabled, setEnabled] = useState<boolean>(initialEnabled);
  // Threshold displayed as dollars in the input.
  const [thresholdDollars, setThresholdDollars] = useState<string>(
    initialThreshold > 0 ? (initialThreshold / 100).toFixed(2) : '',
  );
  const [error, setError] = useState<string | undefined>(undefined);

  // Sync server state on load.
  useEffect(() => {
    setEnabled(initialEnabled);
    setThresholdDollars(
      initialThreshold > 0 ? (initialThreshold / 100).toFixed(2) : '',
    );
  }, [initialEnabled, initialThreshold]);

  if (balanceQ.isLoading || !balanceQ.data) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle>Авто-выплаты</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-9 w-full mb-3" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  const parseThresholdCents = (): number | null => {
    const v = thresholdDollars.trim();
    if (!v) return null;
    const num = Number(v);
    if (!Number.isFinite(num) || num < 0) return null;
    return Math.round(num * 100);
  };

  const handleToggle = () => {
    const nextEnabled = !enabled;
    if (nextEnabled) {
      // Enabling: require threshold >= minPayoutCents.
      const tc = parseThresholdCents() ?? minPayoutCents;
      if (tc < minPayoutCents) {
        setError(
          `Порог не может быть меньше ${formatCents(minPayoutCents)}.`,
        );
        return;
      }
      setError(undefined);
      update.mutate(
        { enabled: true, thresholdCents: tc },
        {
          onSuccess: () => {
            toast.success('Авто-выплаты включены');
            setEnabled(true);
            setThresholdDollars((tc / 100).toFixed(2));
          },
        },
      );
    } else {
      setError(undefined);
      update.mutate(
        { enabled: false },
        {
          onSuccess: () => {
            toast.success('Авто-выплаты выключены');
            setEnabled(false);
          },
        },
      );
    }
  };

  const handleSaveThreshold = () => {
    const tc = parseThresholdCents();
    if (tc == null) {
      setError('Укажи сумму в долларах, например 50.00.');
      return;
    }
    if (tc < minPayoutCents) {
      setError(
        `Порог не может быть меньше ${formatCents(minPayoutCents)}.`,
      );
      return;
    }
    setError(undefined);
    update.mutate(
      { enabled: true, thresholdCents: tc },
      {
        onSuccess: () => {
          toast.success('Настройки сохранены');
        },
      },
    );
  };

  const currentThresholdCents =
    parseThresholdCents() ?? initialThreshold;

  return (
    <Card className="w-full h-full">
      <CardHeader>
        <CardTitle className="truncate min-w-0">Авто-выплаты</CardTitle>
        <span
          className={
            'inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide shrink-0 ' +
            (enabled
              ? 'text-[var(--accent-bright)]'
              : 'text-[var(--text-muted)]')
          }
        >
          <Zap className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{enabled ? 'Активно' : 'Выключено'}</span>
        </span>
      </CardHeader>
      <CardContent>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={handleToggle}
          disabled={update.isPending}
          className={
            'w-full flex items-center justify-between gap-3 px-3 sm:px-4 py-3 rounded-[var(--radius-md)] border motion-base mb-3 min-w-0 ' +
            (enabled
              ? 'border-[var(--accent-soft)] bg-[var(--accent-soft)]/30 text-[var(--text)]'
              : 'border-[var(--border-strong)] bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:bg-[var(--bg-raised)]') +
            ' disabled:opacity-60'
          }
        >
          <span className="text-[13px] font-medium tabular-nums text-left truncate min-w-0">
            {enabled
              ? `Включены: при балансе ≥ ${formatCents(currentThresholdCents)}`
              : 'Авто-выплаты выключены'}
          </span>
          <span
            className={
              'relative inline-block w-9 h-5 rounded-full transition-colors shrink-0 ' +
              (enabled ? 'bg-[var(--accent)]' : 'bg-[var(--bg-base)]')
            }
            aria-hidden="true"
          >
            <span
              className={
                'absolute top-0.5 inline-block w-4 h-4 rounded-full bg-white shadow transition-transform ' +
                (enabled ? 'translate-x-[18px]' : 'translate-x-0.5')
              }
            />
          </span>
        </button>

        {enabled && (
          <div className="min-w-0">
            <label className="block text-[11px] uppercase tracking-[0.14em] font-semibold text-[var(--text-muted)] mb-1.5 truncate">
              Порог авто-выплаты ($)
            </label>
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <Input
                type="number"
                inputMode="decimal"
                step="0.01"
                min={(minPayoutCents / 100).toFixed(2)}
                value={thresholdDollars}
                onChange={(e) => setThresholdDollars(e.target.value)}
                placeholder={(minPayoutCents / 100).toFixed(2)}
                className="tabular-nums min-w-0 flex-1"
              />
              <Button
                variant="secondary"
                size="md"
                onClick={handleSaveThreshold}
                loading={update.isPending}
              >
                Сохранить настройки
              </Button>
            </div>
            <FieldError message={error} />
            <p className="text-[11px] text-[var(--text-muted)] mt-1.5 tabular-nums truncate">
              Минимум — {formatCents(minPayoutCents)}. Когда баланс дойдёт до порога, выплата уйдёт автоматически.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
